import axios from 'axios';
import { API_CONFIG } from '@/config/api.config';
import { encryptData, decryptData } from '@/utils/encryption';
import type { ExtractedArt, IdsOfRecord, UploadedArtKey } from '@/types/ids';

// ===========================================
// IDS (Information Disclosure Statement) API
// ===========================================

export const idsApi = {
  /**
   * Store prior-art PDFs to be read for their document numbers.
   *
   * Multipart rather than the encrypted envelope every other call here uses: these are
   * PDFs, and a scanned patent base64'd through that envelope is both larger and slower.
   * Nothing is read by this call - it stores the files and returns the keys.
   */
  async uploadPriorArt(files: File[]): Promise<{
    batchId: string;
    documents: Array<{ id: string; name: string }>;
    keys: UploadedArtKey[];
  }> {
    const form = new FormData();
    files.forEach(file => form.append('files', file));
    form.append('id_token', localStorage.getItem('id_token') || '');
    const response = await axios.post(
      `${API_CONFIG.baseUrl}/ids/prior_art/upload`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // A set of scanned patents is tens of megabytes on a firm connection.
        timeout: 300000,
      }
    );
    // Not encrypted: this endpoint answers a multipart post in plain JSON.
    return response.data;
  },

  /** Begin reading the uploaded documents. Returns a job id to poll. */
  async startPriorArtExtract(
    keys: UploadedArtKey[],
    appNo?: string
  ): Promise<{ jobId: string; documents: number }> {
    const idToken = localStorage.getItem('id_token');
    const encryptedData = encryptData({ keys, appNo, id_token: idToken });
    const response = await axios.post(
      `${API_CONFIG.baseUrl}/ids/prior_art/extract`,
      { data: encryptedData }
    );
    return decryptData(response.data);
  },

  /** What was read, once the reading has finished. */
  async getPriorArtResult(jobId: string): Promise<{
    status: string;
    error?: string;
    extracted: ExtractedArt | null;
  }> {
    const idToken = localStorage.getItem('id_token');
    const encryptedData = encryptData({ jobId, id_token: idToken });
    const response = await axios.post(
      `${API_CONFIG.baseUrl}/ids/prior_art/result`,
      { data: encryptedData }
    );
    return decryptData(response.data);
  },

  /**
   * Fetch application data by application number
   */
  async fetchAppData(appNo: string): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        appNo,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/fetch_app_data`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to fetch IDS app data:', error);
      throw error;
    }
  },

  /**
   * Fetch application data by docket number
   */
  /**
   * Begin reading what an application has already cited, from its own file wrapper.
   *
   * Separate from generation and runnable before it: a reference already of record need
   * not be cited again (37 CFR 1.98(d)), which changes what goes on the form, so the
   * filer wants the answer while they are still deciding.
   */
  async startOfRecord(appNo: string): Promise<{ jobId: string; appNo: string }> {
    const idToken = localStorage.getItem('id_token');
    const encryptedData = encryptData({ appNo, id_token: idToken });
    const response = await axios.post(
      `${API_CONFIG.baseUrl}/ids/of_record/start`,
      { data: encryptedData }
    );
    return decryptData(response.data);
  },

  /**
   * The result. With a job id it reports that job; without one it returns the most
   * recent read for the application, so re-opening the page does not re-read the
   * wrapper.
   */
  async getOfRecord(input: { appNo?: string; jobId?: string }): Promise<{
    status: string;
    error?: string;
    readAt?: string;
    ofRecord: IdsOfRecord | null;
  }> {
    const idToken = localStorage.getItem('id_token');
    const encryptedData = encryptData({ ...input, id_token: idToken });
    const response = await axios.post(
      `${API_CONFIG.baseUrl}/ids/of_record`,
      { data: encryptedData }
    );
    return decryptData(response.data);
  },

  /**
   * Record that these references are now cited in an application.
   *
   * Offered after an IDS is generated, because that is when it becomes true. Stored as
   * the user's own rather than the wrapper's — until the form is filed and the wrapper
   * re-read, "we filed this" and "the file says this" are different assurances.
   */
  async addToOfRecord(input: {
    appNo: string;
    idsName?: string;
    usPatents?: string[];
    usPublications?: string[];
    foreignPatents?: string[];
    npl?: string[];
  }): Promise<{ added: number; ofRecord: IdsOfRecord | null }> {
    const idToken = localStorage.getItem('id_token');
    const encryptedData = encryptData({ ...input, id_token: idToken });
    const response = await axios.post(
      `${API_CONFIG.baseUrl}/ids/of_record/add`,
      { data: encryptedData }
    );
    return decryptData(response.data);
  },

  async fetchAppDataByDocket(docketNo: string): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        docketNo,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/fetch_app_data_by_docket`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to fetch IDS app data by docket:', error);
      throw error;
    }
  },

  /**
   * Fetch patent family information
   */
  async fetchFamilyData(pubNo: string): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/ids/family/${pubNo}`,
        {
          params: { id_token: idToken },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch IDS family data:', error);
      throw error;
    }
  },

  /**
   * Validate and process patent references
   */
  async validateReferences(references: any): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        references,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/validate_references`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to validate IDS references:', error);
      throw error;
    }
  },

  /**
   * Populate IDS form with processed data
   */
  async populateForm(formData: any): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        ...formData,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/populate`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to populate IDS form:', error);
      throw error;
    }
  },

  /**
   * Find a US application by attorney docket number.
   *
   * The same USPTO search the docket tool uses when adding a matter. A docket number is
   * a prefix by default and is not unique at USPTO, so several matches come back for the
   * filer to choose between rather than one being picked for them.
   */
  async lookupByDocket(dktNo: string): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({ dktNo, id_token: idToken });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/dktNo`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { matches: [], count: 0 };
      }
      console.error('Failed to look up application by docket number:', error);
      throw error;
    }
  },

  /**
   * Submit IDS for async PDF generation
   */
  async submitAsync(idsData: any): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        ...idsData,
        async: 'true',
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/submit`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id || result.jobId || result.id;
    } catch (error: any) {
      console.error('Failed to submit IDS for processing:', error);
      throw error;
    }
  },

  /**
   * Generate IDS form async
   */
  async generateAsync(formData: any): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        ...formData,
        async: 'true',
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/generate`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id;
    } catch (error: any) {
      console.error('Failed to generate IDS form:', error);
      throw error;
    }
  },

  /**
   * Poll job status
   */
  async getJobStatus(jobId: string): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        jobId,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/job_status`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to get IDS job status:', error);
      throw error;
    }
  },

  /**
   * Wait for job completion with polling
   */
  async waitForJobCompletion(
    jobId: string,
    onProgress?: (status: any) => void,
    timeout = 300000
  ): Promise<any> {
    const startTime = Date.now();
    const pollInterval = 2000;

    while (Date.now() - startTime < timeout) {
      const status = await this.getJobStatus(jobId);

      if (onProgress) {
        onProgress(status);
      }

      if (status.status === 'complete') {
        return status;
      }

      if (status.status === 'error' || status.status === 'failed') {
        throw new Error(status.error_message || 'IDS generation failed');
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    throw new Error('IDS generation timed out');
  },

  /**
   * Download completed PDF/ZIP files
   */
  async downloadFile(fileKey: string): Promise<Blob> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        file_key: fileKey,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/file`,
        { data: encryptedData },
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to download IDS file:', error);
      throw error;
    }
  },

  /**
   * Get user's IDS generation history
   */
  async getHistory(): Promise<any[]> {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/ids/history`,
        {
          params: { id_token: idToken },
        }
      );
      return response.data || [];
    } catch (error: any) {
      console.error('Failed to get IDS history:', error);
      throw error;
    }
  },

  /**
   * Delete IDS submission
   */
  async deleteSubmission(idsId: string, time: string): Promise<void> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        idsId,
        time,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/delete`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to delete IDS submission:', error);
      throw error;
    }
  },

  /**
   * Request download of reference PDFs for an IDS submission
   * Returns a job ID that can be polled with waitForJobCompletion
   */
  async downloadReferencePdfs(data: {
    idsName: string;
    usPatents: Array<{ patent_number: string }>;
    usApplications: Array<{ publication_number: string }>;
    foreignPatents: Array<{ country: string; docNumber: string; kind?: string }>;
  }): Promise<string> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        ...data,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/download_reference_pdfs`,
        { data: encryptedData }
      );
      const result = decryptData(response.data);
      return result.job_id;
    } catch (error: any) {
      console.error('Failed to request reference PDF download:', error);
      throw error;
    }
  },

  /**
   * Get IDS settings
   */
  async getSettings(): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/ids/settings`,
        {
          params: { id_token: idToken },
        }
      );
      return response.data || {};
    } catch (error: any) {
      console.error('Failed to get IDS settings:', error);
      throw error;
    }
  },

  /**
   * Update IDS settings
   */
  async updateSettings(settings: any): Promise<any> {
    try {
      const idToken = localStorage.getItem('id_token');
      const encryptedData = encryptData({
        ...settings,
        id_token: idToken,
      });
      const response = await axios.post(
        `${API_CONFIG.baseUrl}/ids/settings/update`,
        { data: encryptedData }
      );
      return decryptData(response.data);
    } catch (error: any) {
      console.error('Failed to update IDS settings:', error);
      throw error;
    }
  },
};
